import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { sendApiRequest } from '../utils';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FiUser, FiPhone, FiCode, FiPackage, FiUsers,
  FiCheckCircle, FiAlertCircle, FiArrowRight,
  FiShield, FiShoppingCart, FiDollarSign,
  FiGrid, FiList, FiInfo, FiDownload, FiCalendar
} from 'react-icons/fi';
import {
  FaGem, FaCrown, FaStar, FaRocket,
  FaHandHoldingUsd, FaChartLine
} from 'react-icons/fa';

const BuyPackage = () => {
  const navigate = useNavigate();
  const userData = useSelector(state => state.user_info[0]?.user);
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    defaultValues: {
      fullName: userData?.fullName || '',
      phone: userData?.phone || '',
      sponsorCode: userData?.sponsorCode || '',
      package: '',
      customer: '',
    }
  });
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const selectedPackageId = watch('package');

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await sendApiRequest('api/packages', {}, 'GET');
        const packagesData = Array.isArray(response.data) ? response.data : [];
        setPackages(packagesData);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        toast.error('Failed to fetch packages');
        setPackages([]);
      }
    };

    const fetchCustomers = async () => {
      try {
        const response = await sendApiRequest('api/customers', {}, 'GET');
        setCustomers(Array.isArray(response.data.data) ? response.data.data : []);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
        toast.error('Failed to fetch customers');
        setCustomers([]);
      }
    };

    fetchCustomers();
    fetchPackages();
  }, []);

  useEffect(() => {
    const selected = packages.find(pkg => pkg._id === selectedPackageId);
    setSelectedPackage(selected);
  }, [selectedPackageId, packages]);

  const onSubmit = async (data) => {
    if (!data.package) {
      toast.error('Please select a package');
      return;
    }
    if (!data.customer) {
      toast.error('Please select a customer');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await sendApiRequest('api/packages/buy', {
        package: data.package,
        customer: data.customer,
      }, 'POST');

      if (response.data.success) {
        toast.success('Package purchase request submitted successfully');
        navigate('/dashboard');
      } else {
        toast.error(response.message || 'Failed to purchase package');
      }
    } catch (error) {
      console.error('Error purchasing package:', error);
      toast.error('Failed to purchase package');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} Lakh`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getPackageBadge = (packageTitle) => {
    if (packageTitle?.toLowerCase().includes('premium')) return { text: 'Premium', color: 'from-amber-500 to-orange-500' };
    if (packageTitle?.toLowerCase().includes('gold')) return { text: 'Gold', color: 'from-yellow-500 to-amber-500' };
    if (packageTitle?.toLowerCase().includes('silver')) return { text: 'Silver', color: 'from-gray-400 to-gray-500' };
    if (packageTitle?.toLowerCase().includes('platinum')) return { text: 'Platinum', color: 'from-blue-500 to-indigo-500' };
    return { text: 'Standard', color: 'from-emerald-500 to-teal-500' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Purchase Package
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Select a package and customer to proceed</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Secure Transaction</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Details Form */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 sticky top-6"
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <FiUser className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white font-semibold text-lg">User Details</h2>
                    <p className="text-indigo-100 text-sm">Verify your information</p>
                  </div>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Full Name
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <FiUser className="w-4 h-4 text-indigo-500" />
                    <span className="text-gray-800 font-medium">{userData?.fullName || 'N/A'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Mobile Number
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <FiPhone className="w-4 h-4 text-indigo-500" />
                    <span className="text-gray-800 font-medium">{userData?.phone || 'N/A'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    User ID
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <FiCode className="w-4 h-4 text-indigo-500" />
                    <span className="text-gray-800 font-mono text-sm">{userData?.sponsorCode || 'N/A'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Select Customer <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register("customer", { required: "Please select a customer" })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 outline-none bg-white"
                    disabled={isSubmitting}
                  >
                    <option value="">Select Customer</option>
                    {customers.length > 0 ? (
                      customers.map((customer) => (
                        <option key={customer._id} value={customer._id}>
                          {customer.customerName} - {customer.phone || ''}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No customers available</option>
                    )}
                  </select>
                  {errors.customer && (
                    <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" />
                      {errors.customer.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Security Notice */}
              <div className="border-t border-gray-100 p-5 bg-gray-50">
                <div className="flex items-start gap-2">
                  <FiShield className="w-4 h-4 text-indigo-500 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Secure Purchase</p>
                    <p className="text-xs text-gray-500 mt-1">Your transaction is encrypted and secure. We never share your information.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Packages Selection */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
            >
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FiPackage className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">Available Packages</h2>
                      <p className="text-indigo-100 text-sm">Choose your investment plan</p>
                    </div>
                  </div>
                  <div className="flex gap-1 bg-white/20 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded-lg transition ${viewMode === 'grid' ? 'bg-white text-indigo-600' : 'text-white'}`}
                    >
                      <FiGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 rounded-lg transition ${viewMode === 'list' ? 'bg-white text-indigo-600' : 'text-white'}`}
                    >
                      <FiList className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-5">
                {/* Hidden input for form validation */}
                <input type="hidden" {...register("package", { required: "Please select a package" })} />
                
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {packages.length > 0 ? (
                      packages.map((pkg, index) => {
                        const badge = getPackageBadge(pkg.title);
                        const isSelected = selectedPackageId === pkg._id;
                        return (
                          <motion.div
                            key={pkg._id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => setValue('package', pkg._id)}
                            className={`cursor-pointer rounded-xl border-2 transition-all duration-300 overflow-hidden ${
                              isSelected 
                                ? 'border-indigo-500 shadow-lg ring-2 ring-indigo-200' 
                                : 'border-gray-200 hover:border-indigo-300 hover:shadow-md'
                            }`}
                          >
                            <div className={`bg-gradient-to-r ${badge.color} px-4 py-2`}>
                              <span className="text-white text-xs font-semibold">{badge.text}</span>
                            </div>
                            <div className="p-4">
                              <h3 className="font-bold text-gray-800 text-lg mb-2">{pkg.title}</h3>
                              <p className="text-2xl font-bold text-indigo-600 mb-3">{formatPrice(pkg.price)}</p>
                              <div className="space-y-2 mb-4">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">Dimensions</span>
                                  <span className="font-semibold text-gray-700">{pkg.width} x {pkg.length}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">Area</span>
                                  <span className="font-semibold text-gray-700">{pkg.area || 'N/A'} sq.ft.</span>
                                </div>
                              </div>
                              {isSelected && (
                                <div className="flex items-center gap-2 text-emerald-600 text-xs mt-2 pt-2 border-t border-gray-100">
                                  <FiCheckCircle className="w-3 h-3" />
                                  <span>Selected</span>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })
                    ) : (
                      <div className="col-span-2 text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                          <FiPackage className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500">No packages available</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {packages.length > 0 ? (
                      packages.map((pkg, index) => {
                        const badge = getPackageBadge(pkg.title);
                        const isSelected = selectedPackageId === pkg._id;
                        return (
                          <motion.div
                            key={pkg._id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => setValue('package', pkg._id)}
                            className={`cursor-pointer rounded-xl border-2 transition-all duration-300 p-4 ${
                              isSelected 
                                ? 'border-indigo-500 bg-indigo-50/30' 
                                : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold text-gray-800">{pkg.title}</h3>
                                  <span className={`text-xs px-2 py-0.5 rounded-full bg-gradient-to-r ${badge.color} text-white`}>
                                    {badge.text}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-4 text-sm">
                                  <span className="text-gray-500">Price: <span className="font-semibold text-indigo-600">{formatPrice(pkg.price)}</span></span>
                                  <span className="text-gray-500">Dimensions: {pkg.width} x {pkg.length}</span>
                                  <span className="text-gray-500">Area: {pkg.area || 'N/A'} sq.ft.</span>
                                </div>
                              </div>
                              {isSelected && (
                                <FiCheckCircle className="w-6 h-6 text-emerald-500" />
                              )}
                            </div>
                          </motion.div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No packages available</p>
                      </div>
                    )}
                  </div>
                )}

                {errors.package && (
                  <p className="mt-3 text-xs text-red-500 flex items-center gap-1">
                    <FiAlertCircle className="w-3 h-3" />
                    {errors.package.message}
                  </p>
                )}

                {/* Selected Package Summary */}
                {selectedPackage && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-100"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <FiInfo className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-indigo-800">Selected Package Summary</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <div>
                            <p className="text-xs text-indigo-600">Package</p>
                            <p className="font-medium text-gray-800">{selectedPackage.title}</p>
                          </div>
                          <div>
                            <p className="text-xs text-indigo-600">Amount</p>
                            <p className="font-medium text-gray-800">{formatPrice(selectedPackage.price)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6"
            >
              <button
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold 
                transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl
                disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FiShoppingCart className="w-5 h-5" />
                    Purchase Package
                    <FiArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </motion.div>

            {/* Terms Notice */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                By proceeding, you agree to our terms and conditions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyPackage;